import { appInsight, collectProperties } from 'components/appInsight';
import { CancelWithdrawalModal } from 'components/cancelWithdrawalModal';
import { DownloadPdfLink } from 'components/downloadPdfLink';
import { IconsEnum } from 'components/Icons';
import { currencyFormatter } from 'components/movimentiTableRow/utils/utils';
import { VoucherPdf } from 'components/voucherPdf';
import format from 'date-fns/format';
import { useGetProfileContractQuery } from 'features/api/apiSlice';
import { useCancelWithdrawalMutation } from 'features/api/userSlice';
import { AlertMessage, setAlertMessage } from 'features/dashboard/dashboardSlice';
import { useInternalSession } from 'hooks';
import { useSendSms } from 'hooks/dashboard/useSendSms';
import { useSendEmail } from 'hooks/dashboard/useSendSmsEmail';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';
import { MovimentiResponseApiItem } from 'lib/api/movimentiResponseApi';
import { useAppDispatch } from 'lib/centralStore';
import { MyAccountCms } from 'lib/datoCms/queries/getMyAccountPage';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { TransactionType } from 'types/dashboard/transactionType';
import { MovimentiStato } from 'types/swagger/movimenti';
import { getAmount } from 'utility/functions';
import { getRouteByKey, PathName } from 'utility/routes';
import styles from './WidgetWithdrawItem.module.scss';

type WidgetWithdrawItemProps = {
  item: MovimentiResponseApiItem;
  cmsData: MyAccountCms;
};

const Button = ({ item, cmsData }: WidgetWithdrawItemProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { stato, tipologia, descrizione, idBiglietto, dataMovimento, dataScadenza, importo, idBigl } = item;

  const dispatch = useAppDispatch();

  const [isOpenAbortWithdrawalModal, setIsOpenAbortWithdrawalModal] = useState(false);
  const [labelScarica, labelGenericError] = useGetLabelsByKeys(['scarica', 'title-generic-error']);

  const {
    session: { user },
  } = useInternalSession();

  // CACHED QUERIES
  const { data: profileContractResponse } = useGetProfileContractQuery(user?.csmfCod, {
    skip: !user?.csmfCod,
  });
  const [cancelWithdrawal] = useCancelWithdrawalMutation();
  // ---

  const sendSms = useSendSms({
    idBigl: idBigl ?? '',
    textSms: cmsData.textSms!,
    importo: currencyFormatter.format(getAmount(importo)),
    transactionDate: dataMovimento ? format(new Date(dataMovimento), 'dd/MM/yyyy') : '',
    expirationDate: dataScadenza ?? '',
  });

  const sendEmail = useSendEmail({
    idBigl: idBigl ?? '',
    textEmail: cmsData.textEmail!,
    subjectMail: cmsData.subjectMail!,
    expirationDate: dataScadenza ?? '',
    importo: currencyFormatter.format(getAmount(importo)),
    transactionDate: dataMovimento ? format(new Date(dataMovimento), 'dd/MM/yyyy') : '',
  });

  const buttonLabel: 'cancel' | 'info' | 'multipleActions' | null = useMemo(() => {
    const isBonifico = descrizione === TransactionType.WithdrawBonifico;

    if (stato === 'In approvazione') {
      return 'cancel';
    }

    if (stato === MovimentiStato.Approvato) {
      if (isBonifico) {
        return 'info';
      }

      // VOUCHER
      return 'multipleActions';
    }

    appInsight?.trackException({
      exception: new Error('Invalid state'),
      properties: collectProperties(true, { stato, tipologia, descrizione }),
    });
    console.error('Unmanaged state');
    return null;
  }, [descrizione, stato, tipologia]);

  const handleClick = useCallback((label: string) => {
    if (label === 'cancel') {
      setIsOpenAbortWithdrawalModal(true);
      return;
    }
    router.push(getRouteByKey(PathName.refillUrl));
  }, []);

  const abortWithdrawal = useCallback(
    async (item: MovimentiResponseApiItem) => {
      const body =
        item.tipologiaPrelievoPending === 'bonifico'
          ? {
              idTicket: item.idBiglietto ?? '',
              codiceCsmf: Number(user?.csmfCod) || 0,
              idRequest: item.idBigl ?? '',
              importo: Number(item.importo) || 0,
              descrizioneCircuito: item.descrizione,
            }
          : {
              idUserSession: crypto.randomUUID(),
              idTransaction: item.idBiglietto,
            };

      const alertMessage: AlertMessage = {
        icon: IconsEnum.CLOSE_CIRCLE,
        message: '',
        color: '',
      };

      try {
        await cancelWithdrawal({ data: body, type: item.tipologiaPrelievoPending ?? 'poste' }).unwrap();
        Object.assign(alertMessage, {
          icon: IconsEnum.SUCCESS,
          message: cmsData.testoAnnullamentoSuccess!,
          color: 'var(--color-success)',
        });
      } catch (e) {
        Object.assign(alertMessage, {
          icon: IconsEnum.CLOSE_CIRCLE,
          message: labelGenericError!,
          color: 'var(--color-warning)',
        });
      } finally {
        dispatch(setAlertMessage(alertMessage));
      }
    },
    [cancelWithdrawal, cmsData.testoAnnullamentoSuccess, dispatch, labelGenericError, user?.csmfCod]
  );

  if (!buttonLabel) return null;

  if (buttonLabel === 'multipleActions') {
    return (
      <div className={styles.multipleFunctionsContainer}>
        {profileContractResponse && (
          <DownloadPdfLink
            className={styles.actionButton}
            document={
              <VoucherPdf
                voucherID={idBigl ?? ''}
                importo={currencyFormatter.format(getAmount(importo))}
                scadenza={dataScadenza ? format(new Date(dataScadenza), 'dd/MM/yyyy') : ''}
                dataRichiesta={dataMovimento ? format(new Date(dataMovimento), 'dd/MM/yyyy HH:mm') : ''}
                userData={profileContractResponse}
                idTransazione={idBiglietto ?? ''}
              />
            }
            fileName={idBigl ?? 'prelievoVoucher'}
          >
            <>{labelScarica} PDF</>
          </DownloadPdfLink>
        )}
        <button onClick={sendSms}>{cmsData.labelWidgetWithdrawSendWith} SMS</button>
        <button onClick={sendEmail}>{cmsData.labelWidgetWithdrawSendWith} mail</button>
      </div>
    );
  }

  return (
    <>
      {isOpenAbortWithdrawalModal && (
        <CancelWithdrawalModal
          open={true}
          item={item}
          title={cmsData.testoConfermaAnnullamento}
          subtitle={cmsData.descrizioneAnnullamento}
          labelAnnulla={cmsData.bottoneTornaIndietro}
          labelConferma={cmsData.bottoneConfermaAnnullamento}
          closeModal={() => setIsOpenAbortWithdrawalModal(false)}
          onOpenChange={() => setIsOpenAbortWithdrawalModal(false)}
          abortWithdrawal={(item: MovimentiResponseApiItem) => abortWithdrawal(item)}
        />
      )}
      <button className={styles.actionButton} onClick={() => handleClick(buttonLabel)}>
        {t(buttonLabel)}
      </button>
    </>
  );
};

export const WidgetWithdrawItem = ({ item, cmsData }: WidgetWithdrawItemProps) => {
  const { dataOrdinamento, descrizione, idBiglietto, stato, importo } = item;
  return (
    <div className={styles.container}>
      <div className={styles.gridContainer}>
        <div className={styles.gridLabels}>
          {dataOrdinamento && (
            <div className={styles.fieldContainerDate}>
              <span>{format(new Date(dataOrdinamento), 'dd/MM/yyyy, HH:mm')}</span>
            </div>
          )}
          {idBiglietto && (
            <p className={styles.ticketLabel}>
              {descrizione && <span>{descrizione}</span>}
              <span>ID: {idBiglietto}</span>
            </p>
          )}
          {stato && <span>{stato}</span>}
          <span className={styles.amount}>- {currencyFormatter.format(getAmount(importo))}</span>
        </div>
        <Button item={item} cmsData={cmsData} />
      </div>
    </div>
  );
};
